import React, { useState, useEffect, useCallback } from "react";
import api from "./api";
import { K } from "./theme";
import { NAV, SROLES } from "./constants/navigation";
import { usePermissions } from "./usePermissions";
import { Login } from "./auth/Login";
import { ForcePasswordChange } from "./auth/ForcePasswordChange";
import { Sidebar } from "./layout/Sidebar";
import { Header } from "./layout/Header";
import { ChatWidget } from "./chat/ChatWidget";
import { NotifyHost } from "./components/NotifyHost";
import { WishReceivedModal } from "./components/WishReceivedModal";
import { ThankYouModal } from "./components/ThankYouModal";
import { notify, promptDialog } from "./notify";
import { Dash } from "./pages/Dashboard";
import { OnboardingPage } from "./pages/Onboarding";
import { DocumentsPage } from "./pages/Documents";
import { HrCornerPage } from "./pages/HrCorner";
import { RecruitmentPage } from "./pages/Recruitment";
import { OrgPage } from "./pages/Organization";
import { SeparationPage } from "./pages/Separation";
import { AppraisalPage } from "./pages/Appraisal";
import { CompliancePage } from "./pages/Compliance";
import { MyProfilePage } from "./pages/MyProfile";
import { AttendancePage } from "./pages/Attendance";
import { LeavesPage } from "./pages/Leaves";
import { PayrollPage } from "./pages/Payroll";
import { ExpensesPage } from "./pages/Expenses";
import { TeamSalaryPage } from "./pages/TeamSalary";
import { AssetsPage } from "./pages/Assets";
import { SettingsPage } from "./pages/Settings";
import { TemplatesPage } from "./pages/Templates";
import { ReportsPage } from "./pages/Reports";
import { EmployeesPage } from "./pages/Employees";
import { CandidatePortal } from "./pages/CandidatePortal";
import { OnboardingJoiningPortal } from "./pages/OnboardingJoiningPortal";
import PublicApply from "./pages/PublicApply";
import PublicIntake from "./pages/PublicIntake";
import PublicExitInterview from "./pages/PublicExitInterview";
import GuestChat from "./pages/GuestChat";

// ═══ APP ═══
export default function App() {
  // Public candidate portal — token in path, no login. Detect FIRST so we
  // never run any auth/data-fetch effects when the URL is /candidate/<token>.
  const portalMatch = window.location.pathname.match(/^\/candidate\/([a-f0-9]{32,64})$/i);
  if (portalMatch) return <CandidatePortal token={portalMatch[1]} />;

  // Public post-hire "Joining Details" form — separate link/token from the
  // recruitment portal above, sent once a candidate is marked Joined.
  const joiningMatch = window.location.pathname.match(/^\/onboarding-form\/([a-f0-9]{32,64})$/i);
  if (joiningMatch) return <OnboardingJoiningPortal token={joiningMatch[1]} />;

  // Public self-apply page — the URL HR shares with candidates. No login,
  // no sidebar. Supported at both /apply and #apply so it works whether
  // the static build sits behind a rewriting proxy (path) or not (hash).
  const applyByPath = window.location.pathname.replace(/\/+$/, '') === '/apply';
  const applyByHash = window.location.hash === '#apply' || window.location.hash.startsWith('#apply?');
  if (applyByPath || applyByHash) return <PublicApply />;

  // Same short-circuit for the employee intake link. Token comes on the
  // query string (path route) or hash query (#intake?token=…).
  const intakeByPath = window.location.pathname.replace(/\/+$/, '') === '/intake';
  const intakeByHash = window.location.hash === '#intake' || window.location.hash.startsWith('#intake?');
  if (intakeByPath || intakeByHash) return <PublicIntake />;

  // Same short-circuit for the exit interview link emailed to an exiting
  // employee — no login by design, so it must be caught here too.
  const exitInterviewByPath = window.location.pathname.replace(/\/+$/, '') === '/exit-interview';
  const exitInterviewByHash = window.location.hash === '#exit-interview' || window.location.hash.startsWith('#exit-interview?');
  if (exitInterviewByPath || exitInterviewByHash) return <PublicExitInterview />;

  // External guest chat invite link — no login, no HRMS access beyond the
  // one group/channel the invite was scoped to (see server/routes/guestChat.js).
  const guestChatByPath = window.location.pathname.replace(/\/+$/, '') === '/guest-chat';
  const guestChatByHash = window.location.hash === '#guest-chat' || window.location.hash.startsWith('#guest-chat?');
  if (guestChatByPath || guestChatByHash) return <GuestChat />;

  // Auth + nav state
  const [token, setToken] = useState(localStorage.getItem('hrms_token'));
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); const [authLoaded, setAuthLoaded] = useState(false);
  const [hasInterviews, setHasInterviews] = useState(false);
  const [userPerms, setUserPerms] = useState(null);
  // Granular (module/tab/field) permissions — new system, only populated
  // for 'dashboard' and 'employees' so far. Fetched once a token exists.
  const perms = usePermissions(!!token, role);
  const NEW_CATALOG_MODULES = ["dashboard", "employees", "myprofile", "attendance", "leaves", "appraisal", "separation", "payroll", "team_salary", "expenses", "compliance", "assets", "documents", "org", "onboarding", "recruitment", "templates", "settings"];
  // Hash can carry a query string (e.g. "#recruitment?tab=requests&id=42",
  // built by requestLink() in recruitment-requests.js and used by any
  // notify() call's `link`) — page state must be just the part before "?",
  // never the raw hash, or every page===<id> check below silently fails
  // and NAV.find() can't match it either (see onHash below).
  const parseHashQuery = (raw) => { const qs = raw.split('?')[1]; const q = {}; if (qs) new URLSearchParams(qs).forEach((v, k) => { q[k] = v; }); return q; };
  // "automation" was removed; old links would hit a dead page.
  const RENAMED_PAGES = { automation: "templates" };
  const canonPage = (p) => RENAMED_PAGES[p] || p;
  const [page, setPage_] = useState(() => { const h = window.location.hash.replace("#", "").split('?')[0]; return canonPage(h || localStorage.getItem("hrms_page") || "dashboard"); });
  const [hashQuery, setHashQuery] = useState(() => parseHashQuery(window.location.hash.replace("#", "")));
  const setPage = (p) => { setPage_(p); localStorage.setItem("hrms_page", p); window.location.hash = p; };
  const [col, setCol] = useState(false);
  const [showN, setShowN] = useState(false);
  const [nfRead, setNfRead] = useState(false);
  const [dashData, setDashData] = useState(null);
  // What's waiting in each module, for the sidebar badges.
  const [pendingCounts, setPendingCounts] = useState({});
  const [notifications, setNotifications] = useState([]);
  // Set when a "celebration" notification (Send Wishes) is clicked — shows
  // who sent it instead of just navigating away. See handleNotifClick.
  const [wishReceived, setWishReceived] = useState(null);
  // Set when a "<name> says thank you…" notification is clicked — the animated thank-you card.
  const [thanksCard, setThanksCard] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState("all");

  // Module data state
  const [myProfile, setMyProfile] = useState(null);
  const [photoCrop, setPhotoCrop] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [lvs, setLvs] = useState([]); const [lvBal, setLvBal] = useState([]); const [lvTeam, setLvTeam] = useState([]); const [lvFreeze, setLvFreeze] = useState({ enabled: false, start: "04-11", end: "04-15", message: "" }); const [settLvTypes, setSettLvTypes] = useState([]);
  const [exps, setExps] = useState([]); const [expCats, setExpCats] = useState([]); const [expTeam, setExpTeam] = useState([]);
  const [astCats, setAstCats] = useState([]); const [astTypeGroups, setAstTypeGroups] = useState([]); const [astMy, setAstMy] = useState([]); const [astInv, setAstInv] = useState([]); const [astReqs, setAstReqs] = useState([]); const [astStats, setAstStats] = useState(null); const [astEligibleEmps, setAstEligibleEmps] = useState([]);
  const [obData, setObData] = useState([]);
  const [spData, setSpData] = useState([]);
  const [attData, setAttData] = useState(null);
  const [apData, setApData] = useState([]); const [apCycle, setApCycle] = useState(null); const [apMy, setApMy] = useState([]); const [apTeam, setApTeam] = useState([]); const [settApCycles, setSettApCycles] = useState([]); const [apQuestions, setApQuestions] = useState([]);
  const [payData, setPayData] = useState(null); const [payReports, setPayReports] = useState(null);
  const [cmpData, setCmpData] = useState(null); const [cmpFilings, setCmpFilings] = useState([]); const [cmpEmps, setCmpEmps] = useState([]);
  const [cnData, setCnData] = useState([]);
  const [docData, setDocData] = useState(null);
  const [holData, setHolData] = useState([]);
  const [settData, setSettData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]); const [offices, setOffices] = useState([]);

  // Hash navigation. Pulled out of the listener so a notification click can
  // call it directly too — setting window.location.hash to a value that
  // equals the CURRENT hash doesn't fire "hashchange" at all (e.g. clicking
  // the same notification twice, or one pointing at the page already open),
  // which otherwise left the page state stuck while the URL bar looked right.
  const navigateToHash = (raw) => {
    if (!raw) return;
    // Standalone public pages (no login, no sidebar — see the early-return
    // checks at the top of this component) render by swapping out this
    // entire component tree, which a plain state update can't do once
    // already mounted and logged in (e.g. pasting an exit-interview link
    // into a tab that already has the dashboard open just fires
    // "hashchange" on the same page, and the fallback below silently sent
    // it to "dashboard" instead). Force a real navigation so the
    // top-of-file checks re-run against a fresh mount.
    if (/^(apply|intake|exit-interview)(\?|$)/.test(raw)) { window.location.reload(); return; }
    const h = canonPage(raw.split('?')[0]);
    setHashQuery(parseHashQuery(raw));
    const nav = NAV.find(n => n.id === h);
    if (!nav) { setPage_("dashboard"); return; }
    setPage_(h);
  };
  useEffect(() => {
    const onHash = () => navigateToHash(window.location.hash.replace("#", ""));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Session expired (401 from api.js) — reset auth state, show Login. No reload.
  useEffect(() => {
    const onAuthExpired = () => {
      setToken(null);
      setUser(null);
      setRole(null);
      setUserPerms(null);
      setAuthLoaded(false);
    };
    window.addEventListener("hrms-auth-expired", onAuthExpired);
    return () => window.removeEventListener("hrms-auth-expired", onAuthExpired);
  }, []);

  // Auth check
  useEffect(() => {
    if (token) {
      let cancelled = false;
      // A thrown error from api.get('/auth/me') means the REQUEST failed
      // outright — network down, or a 502/503 while `pm2 restart` briefly
      // takes the API offline during a deploy — NOT that the token is bad.
      // A genuinely invalid/expired token never reaches this .catch(): it's
      // either silently refreshed (success, handled below) or resolves to
      // `null` after api.js's refresh attempt fails, which already fires
      // hrms-auth-expired itself. Wiping the token here on ANY error used
      // to log out every open tab the instant the backend restarted, even
      // though the session was perfectly valid — so retry through a brief
      // outage instead of discarding a token we have no evidence is bad.
      const loadMe = (attempt = 0) => {
        api.get('/auth/me').then(d => {
          if (cancelled) return;
          if (!d) { setAuthLoaded(true); return; }
          // Fold the linked employee's display name + photo into the user object
          // so the header chip (and dashboard greeting) show the real person —
          // not the login email — and their picture when one is attached.
          const u = d.user || d;
          if (d.employee) {
            if (d.employee.name) u.name = d.employee.name;
            u.profile_photo = d.employee.profile_photo || null;
          }
          setUser(u);
          if (d.user?.system_role) setRole(d.user.system_role);
          else if (d.system_role) setRole(d.system_role);
          if (d.permissions) setUserPerms(d.permissions);
          setAuthLoaded(true);
        }).catch(() => {
          if (cancelled) return;
          // Quick retries cover a normal restart (~1-2s); beyond that, keep
          // retrying more slowly and leave the loading screen up — never
          // clear the token just because the server is momentarily down.
          setTimeout(() => loadMe(attempt + 1), attempt < 4 ? 1500 : 5000);
        });
      };
      loadMe();
      api.get('/api/companies?active=true').then(d => { if (d) setCompanies(Array.isArray(d) ? d : d.data || []); }).catch(console.error);
      // Show "My Interviews" sidebar entry only if the user has at least one assigned round.
      api.get('/api/interview-rounds/my').then(d => setHasInterviews(Array.isArray(d) && d.length > 0)).catch(() => setHasInterviews(false));
      // Fetch the employee profile eagerly so sidebar gates that depend on
      // per-employee flags (can_raise_recruitment, etc.) work on first paint —
      // previously myProfile was only loaded when a specific page opened, so
      // the Recruitment nav item was hidden even for employees who did have
      // permission to see it until they navigated elsewhere first.
      api.get('/api/employees/me').then(d => { if (d) setMyProfile(d); }).catch(console.error);
      return () => { cancelled = true; };
    }
  }, [token]);

  // Dashboard data
  useEffect(() => {
    if (token) {
      api.get('/api/dashboard' + (selCompany && selCompany !== "all" ? "?company=" + selCompany : "")).then(d => {
        if (!d) return;
        setDashData(d);
        setNotifications(d.notifications || []);
      }).catch(console.error);
    }
  }, [token, selCompany]);

  // Notifications — dedicated polling on top of the initial dashboard load
  // so the bell reflects fresh approvals within a minute of them firing.
  // Also exposes refresh + click handlers to Header.
  const refreshNotifications = useCallback(() => {
    if (!token) return;
    api.get('/api/notifications').then(rows => {
      if (Array.isArray(rows)) setNotifications(rows);
    }).catch(() => { });
  }, [token]);
  useEffect(() => {
    if (!token) return;
    refreshNotifications();
    const iv = setInterval(refreshNotifications, 60000);
    return () => clearInterval(iv);
  }, [token, refreshNotifications]);

  // Arriving from the wish email's button (#dashboard?wish=…&from=…): once
  // logged in, open the same "X wished you…" modal a notification click
  // opens, then tidy the query string off the URL.
  useEffect(() => {
    if (!token || !hashQuery.wish) return;
    setWishReceived({ type: hashQuery.wish, fromId: hashQuery.from || null, fromName: hashQuery.fn || 'Team Zucol', fromPhoto: hashQuery.fp || null });
    window.history.replaceState(null, null, '#dashboard');
    setHashQuery({});
  }, [token, hashQuery]);

  // Click a notification → mark it read, then navigate to its deep-link
  // if one was attached at insert time. Kept resilient: no link, no crash.
  const handleNotifClick = useCallback(async (n) => {
    try {
      if (n?.id && !(n.is_read || n.rd)) {
        await api.put('/api/notifications/' + n.id + '/read', {});
      }
    } catch { /* silent */ }
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true, rd: true } : x));
    // A "Send Wishes" notification opens a small "who sent this" modal
    // instead of navigating away — the sender's name/photo travel in the
    // link's query string (set by server/services/celebrationWish.js) so
    // no extra round-trip is needed to show them.
    if (n?.category === 'celebration' && n?.link) {
      const raw = n.link.startsWith('#') ? n.link.slice(1) : n.link;
      const q = parseHashQuery(raw); // URLSearchParams already decodes each value
      setWishReceived({ type: q.wish || 'birthday', fromId: q.from || null, fromName: q.fn || 'Team Zucol', fromPhoto: q.fp || null });
      setShowN(false);
      return;
    }
    // A thank-you for your wishes has nowhere to go (older ones carry a link to the thanker's profile,
    // which most people can't open) — reading it is the whole action.
    if (n?.category === 'celebration_thanks') {
      const q = n.link ? parseHashQuery(n.link.replace(/^#/, '')) : {};
      // Older notifications have no link params — read the name and occasion out of the message text instead.
      const m = String(n.message || n.msg || '').match(/^\S+\s+(.+?) says thank you for your (birthday wishes|work anniversary wishes|anniversary wishes|congratulations)/);
      const type = q.thanks || (m ? (/birthday/.test(m[2]) ? 'birthday' : /anniversary/.test(m[2]) ? 'anniversary' : 'eom') : 'birthday');
      setThanksCard({ type, name: q.fn || (m ? m[1] : ''), photo: q.fp || null });
      setShowN(false);
      return;
    }
    if (n?.link) {
      const raw = n.link.startsWith('#') ? n.link.slice(1) : n.link;
      try { window.location.hash = raw; } catch { /* silent */ }
      navigateToHash(raw);
    }
    setShowN(false);
  }, []);
  // Inline "Approve" button on an asset-request notification. The backend
  // decides whether this stage is a genuine one-click approval (manager
  // stage) or needs more input (HR stage needs an asset + handover photo)
  // — on the latter it 409s with needsMoreInput, and we fall back to the
  // same navigate-to-the-request behavior as clicking the notification body.
  const handleQuickApprove = useCallback(async (n) => {
    try {
      await api.put('/api/notifications/' + n.id + '/read', {});
    } catch { /* silent */ }
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true, rd: true } : x));
    try {
      await api.post('/api/assets/requests/' + n.entity_id + '/quick-approve', {});
      notify('Approved', { title: 'Saved', variant: 'success' });
      if (page === 'assets') {
        api.get('/api/assets/requests').then(d => setAstReqs(d.data || d || [])).catch(console.error);
      }
      setShowN(false);
    } catch (e) {
      if (e?.data?.needsMoreInput) {
        notify('Pick an asset and handover photo to finish this one', { title: 'One more step', variant: 'info' });
        if (n?.link) { const raw = n.link.startsWith('#') ? n.link.slice(1) : n.link; try { window.location.hash = raw; } catch { /* silent */ } navigateToHash(raw); }
        setShowN(false);
      } else if (e?.status === 404) {
        // The request this notification pointed at is gone (deleted,
        // withdrawn, or — most commonly here — stale data left over from
        // before a DB restore). It's already marked read above; a
        // blocking "Error" modal for something the user can't act on is
        // just noise, so this stays a quiet toast instead.
        notify('This request no longer exists — it may have been removed.', { title: 'Not found', variant: 'info' });
      } else {
        notify(e.message || 'Failed to approve', { title: 'Error', variant: 'error' });
      }
    }
  }, [page]);
  // Leave approve/reject always requires a comment (server-enforced), so
  // unlike the asset one-click this prompts for it right here rather than
  // silently trying a no-input call first — same window.prompt pattern the
  // full Leaves page itself already uses for this exact action.
  const handleLeaveAction = useCallback(async (n, action) => {
    const comment = await promptDialog(action === 'approve' ? 'Add a comment for approval:' : 'Reason for rejection:',
      { title: action === 'approve' ? 'Approve leave' : 'Reject leave', multiline: true, variant: action === 'approve' ? 'info' : 'error', okLabel: action === 'approve' ? 'Approve' : 'Reject' });
    if (comment === null) return;
    if (!comment.trim()) { notify('Comment is mandatory', { title: 'Error', variant: 'error' }); return; }
    try {
      await api.put('/api/notifications/' + n.id + '/read', {});
    } catch { /* silent */ }
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true, rd: true } : x));
    try {
      await api.put('/api/leaves/' + n.entity_id + '/' + action, { comment });
      notify(action === 'approve' ? 'Leave approved' : 'Leave rejected', { title: 'Saved', variant: 'success' });
      if (page === 'leaves') {
        const cq = selCompany && selCompany !== "all" ? "company=" + selCompany : "";
        api.get('/api/leaves?view=my' + (cq ? '&' + cq : '')).then(d => setLvs(d.data || d || [])).catch(console.error);
        api.get('/api/leaves?view=team' + (cq ? '&' + cq : '')).then(d => setLvTeam(d.data || d || [])).catch(console.error);
      }
      setShowN(false);
    } catch (e) {
      notify(e.message || `Failed to ${action} leave`, { title: 'Error', variant: 'error' });
    }
  }, [page, selCompany]);
  const handleMarkAllRead = useCallback(async () => {
    try { await api.put('/api/notifications/read-all', {}); } catch { /* silent */ }
    setNotifications(prev => prev.map(x => ({ ...x, is_read: true, rd: true })));
  }, []);

  // Keep the browser tab title in sync with the customer's company name so it
  // doesn't flash "HRMS" after login. Falls back to "HRMS" until data loads.
  // Also stash the app timezone on window so utils/timezone.js picks it up
  // for every date/time render across the app.
  useEffect(() => {
    const nm = dashData?.settings?.company_name || dashData?.company_name;
    document.title = nm ? `${nm} · HRMS` : "HRMS";
    const tz = dashData?.settings?.timezone;
    if (tz) window.__APP_TZ = tz;
  }, [dashData]);

  // Module data loading
  useEffect(() => {
    const cq = selCompany && selCompany !== "all" ? "company=" + selCompany : "";
    const cqp = cq ? "&" + cq : "";
    const cqs = cq ? "?" + cq : "";
    if (page === "myprofile") { api.get('/api/employees/me').then(d => setMyProfile(d)).catch(console.error); }
    if (page === "leaves") {
      api.get('/api/leaves/types').then(d => setSettLvTypes(d.data || d || [])).catch(console.error);
      api.get('/api/leaves/balance').then(d => setLvBal(d.data || d || [])).catch(console.error);
      api.get('/api/leaves/freeze-window').then(d => setLvFreeze(d || { enabled: false, start: "04-11", end: "04-15", message: "" })).catch(() => { });
      api.get('/api/leaves?view=my' + (cq ? '&' + cq : '')).then(d => setLvs(d.data || d || [])).catch(console.error);
      api.get('/api/leaves?view=team' + (cq ? '&' + cq : '')).then(d => setLvTeam(d.data || d || [])).catch(() => setLvTeam([]));
      if (!holData.length) api.get('/api/holidays').then(d => setHolData(d.data || d || [])).catch(console.error);
      if (!myProfile) api.get('/api/employees/me').then(d => setMyProfile(d)).catch(console.error);
    }
    if (page === "expenses") {
      api.get('/api/expenses/categories').then(d => setExpCats(d.data || d || [])).catch(console.error);
      api.get('/api/expenses?view=my' + (cq ? '&' + cq : '')).then(d => setExps(d.data || d || [])).catch(console.error);
      if (perms.can('expenses', 'approvals', null, 'view')) { api.get('/api/expenses?view=pending' + (cq ? '&' + cq : '')).then(d => setExpTeam(d.data || d || [])).catch(console.error); }
    }
    if (page === "employees") {
      if (!offices.length) api.get('/api/offices').then(d => setOffices(Array.isArray(d) ? d : d.data || [])).catch(console.error);
      if (!roles.length) api.get('/api/roles').then(d => setRoles(Array.isArray(d) ? d : d.data || [])).catch(console.error);
      if (!companies.length) api.get('/api/companies?active=true').then(d => setCompanies(Array.isArray(d) ? d : d.data || [])).catch(console.error);
      if (!myProfile) api.get('/api/employees/me').then(d => setMyProfile(d)).catch(console.error);
    }
    if (page === "assets") {
      if (!myProfile) api.get('/api/employees/me').then(d => setMyProfile(d)).catch(console.error);
      api.get('/api/assets/categories').then(d => setAstCats(d.data || d || [])).catch(console.error);
      api.get('/api/assets/type-groups').then(d => setAstTypeGroups(d || [])).catch(console.error);
      api.get('/api/assets/my').then(d => setAstMy(d.data || d || [])).catch(console.error);
      api.get('/api/assets/stats').then(d => setAstStats(d)).catch(console.error);
      if (!offices.length) api.get('/api/offices').then(d => setOffices(Array.isArray(d) ? d : d.data || [])).catch(console.error);
      if (perms.can('assets', 'inventory', null, 'view')) {
        api.get('/api/assets/inventory').then(d => setAstInv(d.data || d || [])).catch(console.error);
        if (!employees.length) api.get('/api/employees?limit=1000').then(d => setEmployees(d.data || [])).catch(console.error);
      }
      if (perms.can('assets', 'requests', 'request_for_others', 'edit')) {
        api.get('/api/employees/asset-eligible').then(d => setAstEligibleEmps(Array.isArray(d) ? d : d.data || [])).catch(console.error);
      }
      if (!myProfile) api.get('/api/employees/me').then(d => setMyProfile(d)).catch(console.error);
      api.get('/api/assets/requests').then(d => setAstReqs(d.data || d || [])).catch(console.error);
    }
    if (page === "onboarding") { if (!roles.length) api.get('/api/roles').then(d => setRoles(Array.isArray(d) ? d : d.data || [])).catch(console.error); if (!offices.length) api.get('/api/offices').then(d => setOffices(Array.isArray(d) ? d : d.data || [])).catch(console.error); if (!departments.length) api.get('/api/departments').then(d => setDepartments(Array.isArray(d) ? d : d.data || [])).catch(console.error); if (!companies.length) api.get('/api/companies?active=true').then(d => setCompanies(Array.isArray(d) ? d : d.data || [])).catch(console.error); }
    if (page === "separation") { api.get('/api/separation' + cqs).then(d => setSpData(d.data || d || [])).catch(console.error); if (!employees.length) api.get('/api/employees?limit=1000' + cqp).then(d => setEmployees(d.data || [])).catch(console.error); }
    if (page === "attendance") {
      api.get('/api/attendance' + cqs).then(d => setAttData(d)).catch(console.error);
      if (!holData.length) api.get('/api/holidays').then(d => setHolData(d.data || d || [])).catch(console.error);
      if (!myProfile) api.get('/api/employees/me').then(d => setMyProfile(d)).catch(console.error);
    }
    if (page === "appraisal") {
      api.get('/api/appraisals/cycles?status=Active').then(d => { const cycs = d.data || d || []; setApCycle(cycs[0] || null); }).catch(console.error);
      api.get('/api/appraisals?view=my' + (cq ? '&' + cq : '')).then(d => setApMy(d.data || d || [])).catch(console.error);
      api.get('/api/appraisals?view=all' + (cq ? '&' + cq : '')).then(d => setApData(d.data || d || [])).catch(console.error);
      if (perms.can('appraisal', 'team', null, 'view')) { api.get('/api/appraisals?view=team' + (cq ? '&' + cq : '')).then(d => setApTeam(d.data || d || [])).catch(console.error); }
      api.get('/api/appraisals/questions').then(d => setApQuestions(d.data || d || [])).catch(console.error);
      if (perms.can('appraisal', 'all', 'open_for_employee', 'edit') && !employees.length) api.get('/api/employees?limit=1000').then(d => setEmployees(d.data || [])).catch(console.error);
      if (!myProfile) api.get('/api/employees/me').then(d => setMyProfile(d)).catch(console.error);
    }
    if (page === "payroll") {
      api.get('/api/payroll' + cqs).then(d => setPayData(d.data || d)).catch(console.error);
      if (perms.can('payroll', 'reports', 'payroll_reports', 'view')) { api.get('/api/payroll/reports').then(d => setPayReports(d.data || d)).catch(console.error); }
      if (!companies.length) api.get('/api/companies?active=true').then(d => setCompanies(Array.isArray(d) ? d : d.data || [])).catch(console.error);
    }
    if (page === "compliance") {
      api.get('/api/compliance' + cqs).then(d => setCmpData(d.data || d)).catch(console.error);
      api.get('/api/compliance/filings').then(d => setCmpFilings(d.data || d || [])).catch(console.error);
      api.get('/api/compliance/employees').then(d => setCmpEmps(d.data || d || [])).catch(console.error);
    }
    if (page === "hrcorner" && !employees.length) { api.get('/api/employees?limit=1000').then(d => setEmployees(d.data || [])).catch(console.error); }
    if (page === "recruitment") { if (!roles.length) api.get('/api/roles').then(d => setRoles(Array.isArray(d) ? d : d.data || [])).catch(console.error); if (!departments.length) api.get('/api/departments').then(d => setDepartments(Array.isArray(d) ? d : d.data || [])).catch(console.error); if (!offices.length) api.get('/api/offices').then(d => setOffices(Array.isArray(d) ? d : d.data || [])).catch(console.error); if (!myProfile) api.get('/api/employees/me').then(d => setMyProfile(d)).catch(console.error); if (!employees.length) api.get('/api/employees?limit=1000').then(d => setEmployees(d.data || [])).catch(console.error); }
    if (page === "documents") { api.get('/api/documents' + cqs).then(d => setDocData(d)).catch(console.error); }
    if (page === "holidays") { api.get('/api/holidays' + cqs).then(d => setHolData(d.data || d || [])).catch(console.error); }
    // This bulk load is for the admin-only Settings tabs (General, Payroll,
    // Compliance, Access Control, ...), most of which 403 for non-admins at
    // the route level anyway.
    if (page === "settings" && perms.can('settings', 'general', 'org_info', 'view')) {
      api.get('/api/settings').then(d => setSettData(d)).catch(console.error);
      api.get('/api/leaves/types').then(d => setSettLvTypes(d.data || d || [])).catch(console.error);
      api.get('/api/appraisals/cycles').then(d => setSettApCycles(d.data || d || [])).catch(console.error);
      api.get('/api/roles').then(d => setRoles(Array.isArray(d) ? d : d.data || [])).catch(console.error);
      api.get('/api/payroll').then(d => setPayData(d.data || d)).catch(console.error);
      api.get('/api/compliance').then(d => setCmpData(d.data || d)).catch(console.error);
      api.get('/api/offices').then(d => setOffices(Array.isArray(d) ? d : d.data || [])).catch(console.error);
      api.get('/api/assets/categories').then(d => setAstCats(d || [])).catch(console.error);
      api.get('/api/assets/type-groups').then(d => setAstTypeGroups(d || [])).catch(console.error);
    }
    // Holidays specifically isn't admin-only (every role can view it — see
    // permissionCatalog's ALL_ROLES on the holidays tab, and the GET route
    // has no role gate), so it needs its own fetch independent of the
    // admin-only block above. Without this, a non-admin (e.g. HR Head)
    // landing on Settings straight away saw a permanently empty Holidays
    // tab unless holData had incidentally already been populated by a
    // prior visit to Leaves/Attendance/the standalone Holidays page.
    if (page === "settings" && !holData.length) {
      api.get('/api/holidays').then(d => setHolData(d.data || d || [])).catch(console.error);
    }
    if (page === "org") {
      api.get('/api/employees?page=1&limit=1000' + cqp).then(d => setEmployees(d.data || [])).catch(console.error);
      api.get('/api/roles').then(d => setRoles(Array.isArray(d) ? d : d.data || [])).catch(console.error);
      api.get('/api/departments').then(d => setDepartments(Array.isArray(d) ? d : d.data || [])).catch(console.error);
    }
    // role is in the deps so role-gated fetches (asset inventory, employees list)
    // re-fire once auth resolves — without this they'd silently no-op on the
    // initial mount when role is still empty.
  }, [page, selCompany, role, perms.loading]);

  const handleLogin = (data) => {
    setToken(data.token);
    setUser(data.user);
    if (data.user?.system_role) setRole(data.user.system_role);
    setPage("dashboard");
  };

  const handleLogout = () => { api.logout(); };
  const loadPendingCounts = () => api.get('/api/dashboard/pending-counts').then(d => setPendingCounts(d || {})).catch(() => { });
  useEffect(() => { if (!token) return; loadPendingCounts(); }, [token, page]); // eslint-disable-line

  const onDashRefresh = () => api.get('/api/dashboard' + (selCompany && selCompany !== "all" ? "?company=" + selCompany : "")).then(d => { if (d) setDashData(d); });

  const NF = notifications;
  const vis = NAV.filter(n => {
    if (!role) return false;
    // An Admin sees every page in the software, whatever the lists or permission data say.
    if (role === 'admin') return true;
    // Reports is granted per area in Settings → Permissions → Reports, so it
    // shows for anyone holding at least one of those (not by role).
    if (n.id === "reports") {
      if (perms.loading) return n.r.includes(role);
      return ["leaves", "attendance", "recruitment", "onboarding", "employees", "activity_log"].some(t => perms.can("reports", t, null, "view"));
    }
    // Settings → Permissions is the only switch: these pages open for anyone granted the
    // specific action, even if their role has no view on the whole module.
    if (!perms.loading) {
      if (n.id === "recruitment" && perms.can('recruitment', 'requests', 'raise_request', 'edit')) return true;
      if (n.id === "expenses" && perms.can('expenses', 'my', 'submit_claim', 'edit')) return true;
      if (n.id === "hrcorner" && perms.can('hrcorner', 'media_hub', null, 'view')) return true;
    }
    // New granular system takes over module-visibility for the two modules
    // it's populated for, once loaded (defer to the old logic below while
    // loading, so the sidebar doesn't flicker an item away and back).
    if (!perms.loading && NEW_CATALOG_MODULES.includes(n.id)) {
      return perms.can(n.id, null, null, "view");
    }
    // Recruitment fallback (no override): visible to HR-side roles, anyone
    // with the Raise Hiring Request permission, or anyone assigned as an interviewer.
    if (n.id === "recruitment") {
      const isHrRecruit = ["admin", "hr_head", "centre_hr", "recruiter", "centre_head"].includes(role);
      if (isHrRecruit) return true;
            if (hasInterviews) return true;
      return false;
    }
    if (n.r !== "all" && n.r.includes(role)) return true;
    if (n.r === "all") return true;
    return false;
  });
  const rObj = SROLES.find(r => r.id === role);
  const unread = nfRead ? 0 : NF.filter(n => !n.is_read && !n.rd).length;

  useEffect(() => { if (role && vis.length > 0 && !vis.find(n => n.id === page)) { setPage_("dashboard"); localStorage.setItem("hrms_page", "dashboard"); window.history.replaceState(null, null, "#dashboard"); } }, [role, userPerms]);

  const userName = user?.name || user?.username || "";
  const userPhoto = user?.profile_photo || null;

  const renderPage = () => {
    if (page === "dashboard") return <Dash role={role} data={dashData} company={selCompany} userName={userName} onNavigate={p => setPage(p)} onNotifClick={handleNotifClick} onQuickApprove={handleQuickApprove} onLeaveAction={handleLeaveAction} can={perms.can} />;
    if (page === "org") return <OrgPage employees={employees} roles={roles} departments={departments} role={role} onRefresh={() => { api.get('/api/roles').then(d => setRoles(Array.isArray(d) ? d : d.data || [])); api.get('/api/departments').then(d => setDepartments(Array.isArray(d) ? d : d.data || [])); api.get('/api/employees?page=1&limit=1000').then(d => setEmployees(d.data || [])); }} can={perms.can} />;
    if (page === "myprofile") return <MyProfilePage myProfile={myProfile} photoCrop={photoCrop} setPhotoCrop={setPhotoCrop} mustChangePassword={!!user?.must_change_password} onPhotoUpdated={() => api.get('/api/employees/me').then(setMyProfile).catch(console.error)} roleLabel={rObj?.l} role={role} can={perms.can} />;
    if (page === "employees") return <EmployeesPage role={role} company={selCompany} companies={companies} offices={offices} myEmployeeId={myProfile?.basic?.employee_id || myProfile?.employee_id} onRefresh={() => { }} hashQuery={hashQuery} can={perms.can} />;
    if (page === "onboarding") return <OnboardingPage roles={roles} offices={offices} companies={companies} departments={departments} role={role} onRefresh={() => { }} hashQuery={hashQuery} can={perms.can} />;
    if (page === "separation") return <SeparationPage spData={spData} employees={employees} role={role} company={selCompany} hashQuery={hashQuery} myEmployeeId={myProfile?.basic?.employee_id || myProfile?.employee_id} onRefresh={() => api.get('/api/separation' + (selCompany && selCompany !== 'all' ? '?company=' + selCompany : '')).then(d => setSpData(d.data || d || []))} can={perms.can} />;
    if (page === "attendance") return <AttendancePage attData={attData} myProfile={myProfile} holData={holData} role={role} company={selCompany} hasReports={user?.has_reports} onRefresh={() => api.get('/api/attendance').then(d => setAttData(d))} can={perms.can} hashQuery={hashQuery} clearHashQuery={() => { setHashQuery({}); window.history.replaceState(null, null, "#attendance"); }} />;
    if (page === "leaves") return <LeavesPage lvs={lvs} lvBal={lvBal} lvTeam={lvTeam} lvTypes={settLvTypes} lvFreeze={lvFreeze} holData={holData} myProfile={myProfile} role={role} company={selCompany} hashQuery={hashQuery} onNavigate={p => setPage(p)} onRefresh={() => { api.get('/api/leaves?view=my').then(d => setLvs(d.data || d || [])); api.get('/api/leaves/balance').then(d => setLvBal(d.data || d || [])); api.get('/api/leaves?view=team').then(d => setLvTeam(d.data || d || [])).catch(() => { }); }} can={perms.can} />;
    if (page === "appraisal") return <AppraisalPage apData={apData} apCycle={apCycle} apMy={apMy} apTeam={apTeam} apQuestions={apQuestions} employees={employees} myEmployeeId={myProfile?.basic?.employee_id || myProfile?.employee_id} role={role} hashQuery={hashQuery} onRefresh={() => { api.get('/api/appraisals?view=my').then(d => setApMy(d.data || d || [])).catch(console.error); api.get('/api/appraisals?view=team').then(d => setApTeam(d.data || d || [])).catch(console.error); api.get('/api/appraisals?view=all').then(d => setApData(d.data || d || [])).catch(console.error); api.get('/api/appraisals/cycles?status=Active').then(d => { const cycs = d.data || d || []; setApCycle(cycs[0] || null); }).catch(console.error); }} can={perms.can} />;
    if (page === "payroll") return <PayrollPage payData={payData} payReports={payReports} role={role} companies={companies} onNavigate={p => setPage(p)} onRefresh={() => { api.get('/api/payroll').then(d => setPayData(d.data || d)); api.get('/api/payroll/reports').then(d => setPayReports(d.data || d)); }} can={perms.can} />;
    if (page === "compliance") return <CompliancePage cmpData={cmpData} cmpFilings={cmpFilings} cmpEmps={cmpEmps} role={role} onRefresh={() => { api.get('/api/compliance').then(d => setCmpData(d.data || d)); api.get('/api/compliance/filings').then(d => setCmpFilings(d.data || d || [])); api.get('/api/compliance/employees').then(d => setCmpEmps(d.data || d || [])); }} can={perms.can} />;
    if (page === "team_salary") return <TeamSalaryPage role={role} />;
    if (page === "expenses") return <ExpensesPage exps={exps} expTeam={expTeam} expCats={expCats} role={role} company={selCompany} myProfile={myProfile} hashQuery={hashQuery} onRefresh={() => { api.get('/api/expenses?view=my').then(d => setExps(d.data || d || [])); if (['admin', 'centre_head'].includes(role)) api.get('/api/expenses?view=pending').then(d => setExpTeam(d.data || d || [])); }} can={perms.can} />;
    if (page === "assets") return <AssetsPage astMy={astMy} astInv={astInv} astStats={astStats} astReqs={astReqs} astCats={astCats} astTypeGroups={astTypeGroups} offices={offices} employees={employees} assignableEmployees={astEligibleEmps} myProfile={myProfile} hashQuery={hashQuery} role={role} onRefresh={() => { api.get('/api/assets/my').then(d => setAstMy(d.data || d || [])); api.get('/api/assets/stats').then(d => setAstStats(d)).catch(() => { }); if (['admin', 'hr_head', 'centre_hr'].includes(role)) api.get('/api/assets/inventory').then(d => setAstInv(d.data || d || [])); if (['admin', 'hr_head', 'centre_hr'].includes(role)) api.get('/api/employees/asset-eligible').then(d => setAstEligibleEmps(Array.isArray(d) ? d : d.data || [])).catch(() => { }); api.get('/api/assets/requests').then(d => setAstReqs(d.data || d || [])); }} can={perms.can} />;
    if (page === "documents") return <DocumentsPage docData={docData} role={role} onRefresh={() => api.get("/api/documents").then(d => setDocData(d.data || d))} can={perms.can} />;
    if (page === "hrcorner") return <HrCornerPage role={role} myProfile={myProfile} canMediaHub={perms.can('hrcorner', 'media_hub', null, 'view')} canDeleteEvents={perms.can('hrcorner', 'media_hub', 'delete_event', 'edit')} companies={companies} offices={offices} employees={employees} hashQuery={hashQuery} />;
    if (page === "recruitment") return <RecruitmentPage roles={roles} role={role} companies={companies} departments={departments} offices={offices} employees={employees} myProfile={myProfile} hasInterviews={hasInterviews} hashQuery={hashQuery} onRefresh={() => { }} can={perms.can} />;
    if (page === "templates") return <TemplatesPage role={role} />;
    if (page === "reports") return <ReportsPage />;
    if (page === "settings") return <SettingsPage settData={settData} settLvTypes={settLvTypes} settApCycles={settApCycles} cmpData={cmpData} payData={payData} holData={holData} offices={offices} companies={companies} dashData={dashData} assetCats={astCats} assetTypeGroups={astTypeGroups} role={role} can={perms.can} onRefresh={onDashRefresh} onLvTypesChange={setSettLvTypes} onApCyclesChange={setSettApCycles} onHolDataChange={setHolData} onOfficesChange={setOffices} onCompaniesChange={setCompanies} onSettDataChange={setSettData} onAssetCatsChange={setAstCats} onAssetTypeGroupsChange={setAstTypeGroups} />;
    return <div style={{ textAlign: "center", padding: 40, color: K.t3 }}>Module: {page}</div>;
  };

  if (!token) return <><NotifyHost /><Login onLogin={handleLogin} /></>;
  // Signing in with the welcome / reset password: the person must choose their own password before anything else is
  // shown (the server also refuses every other request until they do).
  if (!authLoaded) return (
    <div className="flex h-screen items-center justify-center bg-bg">
      <div className="text-center">
        <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-[14px] font-extrabold text-white mx-auto mb-3" style={{ background: `linear-gradient(135deg,${K.p},${K.tl})` }}>HR</div>
        <div className="text-[14px] text-t3">Loading...</div>
      </div>
    </div>
  );

  if (user?.must_change_password) return (
    <><NotifyHost /><ForcePasswordChange username={user.username} onDone={() => window.location.reload()} onLogout={handleLogout} /></>
  );

  return (
    <div className="flex h-screen bg-bg text-t1 overflow-hidden">
      {/* Global imperative notify/confirm host. Replaces window.alert
          and window.confirm with our own modal — see src/notify.js. */}
      <NotifyHost />
      <WishReceivedModal item={wishReceived} onClose={() => setWishReceived(null)} />
      <ThankYouModal item={thanksCard} onClose={() => setThanksCard(null)} />
      {/* Default-on: shows while permissions are still loading and for
          anyone not explicitly denied — Settings > Permissions seeds every
          role with 'chat' view access, so hiding it is an opt-out an admin
          has to take, not something anyone has to be opted into first. */}
      {(perms.loading || perms.can('chat', null, null, 'view')) && <ChatWidget myUserId={user?.id} isAdmin={perms.can('settings', 'general', 'google_calendar_default', 'edit')} />}
      <Sidebar col={col} setCol={setCol} vis={vis} page={page} setPage={setPage} dashData={dashData} pendingCounts={pendingCounts} onLogout={handleLogout} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          companies={companies} selCompany={selCompany} setSelCompany={setSelCompany}
          showN={showN} setShowN={setShowN}
          nfRead={nfRead} setNfRead={setNfRead}
          notifications={NF} unread={unread}
          onNotifClick={handleNotifClick}
          onQuickApprove={handleQuickApprove}
          onLeaveAction={handleLeaveAction}
          onMarkAllRead={handleMarkAllRead}
          onRefreshNotifications={refreshNotifications}
          userName={userName} userPhoto={userPhoto} roleLabel={rObj?.l}
          role={role} canPickCompany={perms.can('employees', 'basic', null, 'edit')} onGoToMyProfile={() => setPage("myprofile")}
          onGoToProfile={(empId) => { window.location.hash = 'employees?id=' + encodeURIComponent(empId); }}
        />
        <main className="flex-1 overflow-auto p-2 sm:p-3 lg:p-[18px] bg-bg" onClick={() => { if (showN) setShowN(false); }}>
          <div key={page} className="animate-fadeIn">
            {renderPage()}
          </div>
        </main>
      </div>
    </div>
  );
}
